import { useContext, createContext, useState, useMemo, FC } from 'react';
import noop from 'utils/noop';
import { CompleteQuery } from 'modules/queryBuilder/types';
import { CatalogPreviewResult } from '../types';
import { useCatalogPreviewResults } from '../hooks/useCatalogPreviewResults';

export enum Preview {
  open = 'open',
  closed = 'closed',
}

type CatalogPreviewState = {
  isOpen: boolean;
  closePreview: () => void;
  openPreview: () => void;
  previewResults: (searchQuery: CompleteQuery) => void;
  catalogPreviewResults: CatalogPreviewResult[];
  isLoading: boolean;
};

export const defaultCatalogPreviewState = {
  isOpen: false,
  closePreview: noop,
  openPreview: noop,
  isLoading: false,
  catalogPreviewResults: [],
  previewResults: noop,
};

export const CatalogPreviewContext = createContext<CatalogPreviewState>(defaultCatalogPreviewState);
export const useCatalogPreviewContext = () => useContext(CatalogPreviewContext);

export const CatalogPreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  /** handle page opening */
  const { previewResults, catalogPreviewResults, isLoading } = useCatalogPreviewResults();
  const [currentPreview, changePreview] = useState<Preview>(Preview.closed);
  const isOpen = useMemo(() => currentPreview === Preview.open, [currentPreview]);
  const closePreview = () => changePreview(Preview.closed);
  const openPreview = () => changePreview(Preview.open);

  const value = {
    isOpen,
    closePreview,
    openPreview,
    previewResults,
    catalogPreviewResults,
    isLoading,
  };

  return <CatalogPreviewContext.Provider value={value}>{children}</CatalogPreviewContext.Provider>;
};
